// apiSourcesConfig.ts
// Configuration for API Sources feature - endpoint definitions for all APIs

import { ApiConfig } from '../types/apiSources';

// Note: Full endpoint configurations extracted from Disha's HTML files
// Each API has categories with endpoints including method, path, description, status, and fields

export const API_SOURCES_CONFIG: Record<string, ApiConfig> = {
  aquarius: {
    id: 'aquarius',
    name: 'Aquarius',
    description: 'Driver Management & Compliance System - Tachograph data, driver activity tracking, compliance monitoring, asset management, and defect checks',
    baseUrl: 'https://portal.aquariusit.com/data_api_v2',
    authMethod: 'OAuth2 Password Grant',
    themeColor: '#f97316',
    totalEndpoints: 43,
    workingEndpoints: 34,
    categories: [
      {
        id: 'driver-activity',
        name: 'Driver Activity',
        endpoints: [
          {
            id: 'activity-records.php',
            method: 'POST',
            path: '/activity-records.php',
            category: 'driver-activity',
            description: 'Returns activity records - driving, rest, availability, other work from tachograph',
            status: 'working',
            requiresDateRange: true,
            fields: [
              { name: 'emp_id', type: 'int', description: 'Employee ID' },
              { name: 'name', type: 'string', description: 'Driver full name' },
              { name: 'pay_no', type: 'string', description: 'Payroll number' },
              { name: 'lic_no', type: 'string', description: 'Driver license number' },
              { name: 'crd_id', type: 'string', description: 'Tachograph card ID' },
              { name: 'reg', type: 'string', description: 'Vehicle registration' },
              { name: 'act', type: 'char', description: 'Activity code: d=driving, r=rest, a=available, o=other work, b=break' },
              { name: 'st_tme', type: 'datetime', description: 'Activity start time' },
              { name: 'end_tme', type: 'datetime', description: 'Activity end time' },
              { name: 'edit_tme', type: 'datetime', description: 'Last edit timestamp' }
            ]
          },
          {
            id: 'driver-hub.php',
            method: 'POST',
            path: '/driver-hub.php',
            category: 'driver-activity',
            description: 'Driver hub data - comprehensive driver information hub',
            status: 'failed',
            requiresDateRange: true
          },
          {
            id: 'shift-summary.php',
            method: 'POST',
            path: '/shift-summary.php',
            category: 'driver-activity',
            description: 'Shift summary - working periods and breaks analysis',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'ta-shift-data.php',
            method: 'POST',
            path: '/ta-shift-data.php',
            category: 'driver-activity',
            description: 'Time & Attendance shift start/end (fills gaps when activity-records are empty)',
            status: 'working',
            requiresDateRange: true,
            fields: [
              { name: 'fname', type: 'string', description: 'First name' },
              { name: 'sname', type: 'string', description: 'Surname' },
              { name: 'card_id', type: 'string', description: 'Tachograph card ID' },
              { name: 'start_time', type: 'datetime', description: 'Shift start' },
              { name: 'end_time', type: 'datetime', description: 'Shift end' },
              { name: 'shift_mins', type: 'int', description: 'Shift duration in minutes' },
              { name: 'gps_out', type: 'string', description: 'Clock-out GPS lat lon' },
              { name: 'gps_in', type: 'string', description: 'Clock-in GPS lat lon' }
            ]
          }
        ]
      },
      {
        id: 'drivers',
        name: 'Drivers',
        endpoints: [
          {
            id: 'card-id-driver-info.php',
            method: 'POST',
            path: '/card-id-driver-info.php',
            category: 'drivers',
            description: 'Driver details by card ID - full driver profile with card information',
            status: 'working',
            fields: [
              { name: 'emp_id', type: 'int', description: 'Employee ID' },
              { name: 'payroll_no', type: 'string', description: 'Payroll number' },
              { name: 'fname', type: 'string', description: 'First name' },
              { name: 'sname', type: 'string', description: 'Surname' },
              { name: 'card_id', type: 'string', description: 'Tachograph card ID' },
              { name: 'licence_no', type: 'string', description: 'Driver license number' }
            ]
          },
          {
            id: 'driver-download-history.php',
            method: 'POST',
            path: '/driver-download-history.php',
            category: 'drivers',
            description: 'Driver download history - tachograph card downloads',
            status: 'failed'
          },
          {
            id: 'holiday-status.php',
            method: 'POST',
            path: '/holiday-status.php',
            category: 'drivers',
            description: 'Holiday status - driver leave and holiday tracking',
            status: 'failed'
          }
        ]
      },
      {
        id: 'compliance',
        name: 'Compliance',
        endpoints: [
          {
            id: 'audit-records.php',
            method: 'POST',
            path: '/audit-records.php',
            category: 'compliance',
            description: 'Audit trail records - system changes and updates',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'driver-violations.php',
            method: 'POST',
            path: '/driver-violations.php',
            category: 'compliance',
            description: 'Driver violations - hours exceeded, regulation breaches',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'driver-compliance-summary.php',
            method: 'POST',
            path: '/driver-compliance-summary.php',
            category: 'compliance',
            description: 'Driver compliance summary - overview of compliance status',
            status: 'working'
          },
          {
            id: 'vu-overspeed-data.php',
            method: 'POST',
            path: '/vu-overspeed-data.php',
            category: 'compliance',
            description: 'VU overspeed data - tachograph overspeed events',
            status: 'working'
          }
        ]
      },
      {
        id: 'assets',
        name: 'Assets',
        endpoints: [
          {
            id: 'asset-list.php',
            method: 'POST',
            path: '/asset-list.php',
            category: 'assets',
            description: 'List of all assets - vehicles and trailers',
            status: 'working'
          },
          {
            id: 'asset-details.php',
            method: 'POST',
            path: '/asset-details.php',
            category: 'assets',
            description: 'Asset details - full asset information',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'current-vor-assets.php',
            method: 'POST',
            path: '/current-vor-assets.php',
            category: 'assets',
            description: 'Current VOR (Vehicle Off Road) assets',
            status: 'working'
          }
        ]
      },
      {
        id: 'maintenance',
        name: 'Maintenance',
        endpoints: [
          {
            id: 'driver-defect-checks.php',
            method: 'POST',
            path: '/driver-defect-checks.php',
            category: 'maintenance',
            description: 'Driver defect checks - vehicle walkaround inspections',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'defect-check-history.php',
            method: 'POST',
            path: '/defect-check-history.php',
            category: 'maintenance',
            description: 'Defect check history - historical checks',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'outstanding-defects.php',
            method: 'POST',
            path: '/outstanding-defects.php',
            category: 'maintenance',
            description: 'Outstanding defects awaiting repair',
            status: 'working'
          }
        ]
      },
      {
        id: 'events',
        name: 'Events',
        endpoints: [
          {
            id: 'late-events.php',
            method: 'POST',
            path: '/late-events.php',
            category: 'events',
            description: 'Late events - overdue scheduled items',
            status: 'working'
          },
          {
            id: 'upcoming-events-detail.php',
            method: 'POST',
            path: '/upcoming-events-detail.php',
            category: 'events',
            description: 'Upcoming events detail - scheduled maintenance and compliance',
            status: 'working'
          },
          {
            id: 'completed-events-detail.php',
            method: 'POST',
            path: '/completed-events-detail.php',
            category: 'events',
            description: 'Completed events detail - recently finished maintenance',
            status: 'working',
            requiresDateRange: true
          }
        ]
      }
    ]
  },
  
  jigsaw: {
    id: 'jigsaw',
    name: 'Jigsaw',
    description: 'Fuel Card Management System - Fuel card tracking, tank levels, fuel purchases, driver/vehicle fuel access',
    baseUrl: 'https://fuelapi.jigsawm2m.com/api/v1',
    authMethod: 'JWT Bearer Token',
    themeColor: '#ec4899',
    totalEndpoints: 34,
    workingEndpoints: 26,
    categories: [
      {
        id: 'company',
        name: 'Company',
        endpoints: [
          {
            id: 'company-products',
            method: 'GET',
            path: '/company/products',
            category: 'company',
            description: 'List all company fuel products',
            status: 'working'
          },
          {
            id: 'company-sites',
            method: 'GET',
            path: '/company/sites',
            category: 'company',
            description: 'List all company fuel sites',
            status: 'working'
          },
          {
            id: 'company-depots',
            method: 'GET',
            path: '/company/depots',
            category: 'company',
            description: 'List all company depots',
            status: 'working'
          }
        ]
      },
      {
        id: 'drivers',
        name: 'Drivers',
        endpoints: [
          {
            id: 'drivers-list',
            method: 'GET',
            path: '/drivers',
            category: 'drivers',
            description: 'List all fuel card drivers',
            status: 'working'
          },
          {
            id: 'driver-by-id',
            method: 'GET',
            path: '/drivers/{id}',
            category: 'drivers',
            description: 'Get driver by ID',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'driver-by-tag',
            method: 'GET',
            path: '/drivers/tag/{tagNumber}',
            category: 'drivers',
            description: 'Get driver by tag number',
            status: 'working',
            requiresParams: true
          }
        ]
      },
      {
        id: 'groups',
        name: 'Groups',
        endpoints: [
          {
            id: 'vehicle-groups',
            method: 'GET',
            path: '/groups/vehicles',
            category: 'groups',
            description: 'List vehicle groups',
            status: 'working'
          },
          {
            id: 'driver-groups',
            method: 'GET',
            path: '/groups/drivers',
            category: 'groups',
            description: 'List driver groups',
            status: 'working'
          }
        ]
      },
      {
        id: 'tanks',
        name: 'Tanks',
        endpoints: [
          {
            id: 'tanks-list',
            method: 'GET',
            path: '/tanks',
            category: 'tanks',
            description: 'List all fuel tanks',
            status: 'working'
          },
          {
            id: 'tank-stock-history',
            method: 'GET',
            path: '/tanks/{id}/stock-history',
            category: 'tanks',
            description: 'Get tank stock history',
            status: 'working',
            requiresParams: true,
            requiresDateRange: true
          },
          {
            id: 'tank-graph-data',
            method: 'GET',
            path: '/tanks/{id}/graph',
            category: 'tanks',
            description: 'Get tank level graph data',
            status: 'working',
            requiresParams: true
          }
        ]
      },
      {
        id: 'vehicles',
        name: 'Vehicles',
        endpoints: [
          {
            id: 'vehicles-list',
            method: 'GET',
            path: '/vehicles',
            category: 'vehicles',
            description: 'List all vehicles',
            status: 'working'
          },
          {
            id: 'vehicle-by-tag',
            method: 'GET',
            path: '/vehicles/tag/{tagNumber}',
            category: 'vehicles',
            description: 'Get vehicle by tag number',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'vehicle-by-id',
            method: 'GET',
            path: '/vehicles/{id}',
            category: 'vehicles',
            description: 'Get vehicle by ID',
            status: 'working',
            requiresParams: true
          }
        ]
      },
      {
        id: 'transactions',
        name: 'Transactions',
        endpoints: [
          {
            id: 'offsite-transactions',
            method: 'GET',
            path: '/transactions/offsite',
            category: 'transactions',
            description: 'Get offsite fuel transactions',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'transaction-by-id',
            method: 'GET',
            path: '/transactions/{id}',
            category: 'transactions',
            description: 'Get transaction by ID',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'transaction-listing',
            method: 'GET',
            path: '/reporting/transaction-listing',
            category: 'transactions',
            description: 'Get transaction listing report',
            status: 'working',
            requiresDateRange: true
          }
        ]
      }
    ]
  },
  
  optifleet: {
    id: 'optifleet',
    name: 'Optifleet (Volvo Group)',
    description: 'Fleet Telematics & GPS Tracking - Real-time GPS tracking, vehicle positions, driver times, eco-scores, alerts, tachograph',
    baseUrl: 'https://api.renault-trucks.com/vehicle',
    authMethod: 'Basic Auth + OAuth2',
    themeColor: '#3b82f6',
    totalEndpoints: 21,
    workingEndpoints: 8,
    categories: [
      {
        id: 'vehicles',
        name: 'Vehicle Data',
        endpoints: [
          {
            id: 'vehicles-list',
            method: 'GET',
            path: '/vehicles',
            category: 'vehicles',
            description: 'List all tracked vehicles',
            status: 'working'
          },
          {
            id: 'vehicle-positions',
            method: 'GET',
            path: '/vehicles/positions',
            category: 'vehicles',
            description: 'Get current vehicle positions',
            status: 'working'
          },
          {
            id: 'vehicle-status',
            method: 'GET',
            path: '/vehicles/{id}/status',
            category: 'vehicles',
            description: 'Get vehicle status',
            status: 'failed',
            requiresParams: true
          }
        ]
      },
      {
        id: 'drivers',
        name: 'Driver Management',
        endpoints: [
          {
            id: 'drivers-list',
            method: 'GET',
            path: '/drivers',
            category: 'drivers',
            description: 'List all drivers',
            status: 'working'
          },
          {
            id: 'driver-times',
            method: 'GET',
            path: '/drivers/{id}/times',
            category: 'drivers',
            description: 'Get driver working times',
            status: 'failed',
            requiresParams: true
          }
        ]
      },
      {
        id: 'analytics',
        name: 'Analytics',
        endpoints: [
          {
            id: 'eco-scores',
            method: 'GET',
            path: '/analytics/eco-scores',
            category: 'analytics',
            description: 'Get vehicle eco-driving scores',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'utilization',
            method: 'GET',
            path: '/analytics/utilization',
            category: 'analytics',
            description: 'Get fleet utilization statistics',
            status: 'working',
            requiresDateRange: true
          }
        ]
      },
      {
        id: 'alerts',
        name: 'Safety & Alerts',
        endpoints: [
          {
            id: 'vehicle-alerts',
            method: 'GET',
            path: '/alerts',
            category: 'alerts',
            description: 'Get vehicle alerts (speeding, harsh braking)',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'geofence-alerts',
            method: 'GET',
            path: '/alerts/geofence',
            category: 'alerts',
            description: 'Get geofence breach alerts',
            status: 'failed',
            requiresDateRange: true
          }
        ]
      },
      {
        id: 'tachograph',
        name: 'Compliance',
        endpoints: [
          {
            id: 'tachograph-files',
            method: 'GET',
            path: '/tachograph/files',
            category: 'tachograph',
            description: 'Get tachograph file list',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'tachograph-download',
            method: 'GET',
            path: '/tachograph/files/{id}/download',
            category: 'tachograph',
            description: 'Download tachograph file',
            status: 'failed',
            requiresParams: true
          }
        ]
      }
    ]
  },
  
  qargo: {
    id: 'qargo',
    name: 'Qargo',
    description: 'Transport Management System - Transport order management, trips, resources, companies, documents',
    baseUrl: 'https://api.qargo.com/v1',
    authMethod: 'OAuth2 Client Credentials',
    themeColor: '#22c55e',
    totalEndpoints: 17,
    workingEndpoints: 8,
    categories: [
      {
        id: 'orders',
        name: 'Order Management',
        endpoints: [
          {
            id: 'orders-list',
            method: 'GET',
            path: '/orders',
            category: 'orders',
            description: 'List all transport orders',
            status: 'working',
            requiresDateRange: true
          },
          {
            id: 'order-details',
            method: 'GET',
            path: '/orders/{id}',
            category: 'orders',
            description: 'Get order details',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'order-status',
            method: 'GET',
            path: '/orders/{id}/status',
            category: 'orders',
            description: 'Get order status',
            status: 'failed',
            requiresParams: true
          },
          {
            id: 'order-documents',
            method: 'GET',
            path: '/orders/{id}/documents',
            category: 'orders',
            description: 'Get order documents',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'order-charges',
            method: 'GET',
            path: '/orders/{id}/charges',
            category: 'orders',
            description: 'List order charges - pricing breakdown',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'export-orders',
            method: 'POST',
            path: '/orders/export',
            category: 'orders',
            description: 'Export orders data',
            status: 'working',
            requiresDateRange: true
          }
        ]
      },
      {
        id: 'resources',
        name: 'Resource Management',
        endpoints: [
          {
            id: 'resources-list',
            method: 'GET',
            path: '/resources',
            category: 'resources',
            description: 'List all resources (drivers, vehicles)',
            status: 'working'
          },
          {
            id: 'resource-details',
            method: 'GET',
            path: '/resources/{id}',
            category: 'resources',
            description: 'Get resource details',
            status: 'working',
            requiresParams: true
          },
          {
            id: 'resource-unavailability',
            method: 'GET',
            path: '/resources/{id}/unavailability',
            category: 'resources',
            description: 'Get resource unavailability periods',
            status: 'failed',
            requiresParams: true
          }
        ]
      },
      {
        id: 'accounting',
        name: 'Accounting',
        endpoints: [
          {
            id: 'sync-tasks',
            method: 'GET',
            path: '/accounting/sync-tasks',
            category: 'accounting',
            description: 'Get accounting sync tasks',
            status: 'failed'
          },
          {
            id: 'companies',
            method: 'GET',
            path: '/accounting/companies',
            category: 'accounting',
            description: 'List accounting companies',
            status: 'failed'
          },
          {
            id: 'invoices',
            method: 'GET',
            path: '/accounting/invoices',
            category: 'accounting',
            description: 'List invoices',
            status: 'failed',
            requiresDateRange: true
          }
        ]
      },
      {
        id: 'documents',
        name: 'Document Management',
        endpoints: [
          {
            id: 'download-document',
            method: 'GET',
            path: '/documents/{id}/download',
            category: 'documents',
            description: 'Download document file',
            status: 'working',
            requiresParams: true
          }
        ]
      }
    ]
  }
};

export const API_SOURCE_IDS = ['aquarius', 'jigsaw', 'optifleet', 'qargo'] as const;
